import {
  checkinWeekOf,
  usualTrainingHour,
  weeklyCheckinDue,
  workoutReminderDue,
  type Hour,
} from '@cadencia/domain';
import { db } from '../db.js';
import { getPreferences } from '../repositories/profileRepo.js';
import { getCheckin } from '../repositories/progressRepo.js';
import { getSessionByDate } from '../repositories/trainingRepo.js';
import { todayLocal } from '../../lib/dates.js';
import { logger } from '../../lib/logger.js';
import {
  CANAL,
  NOTIFICACION,
  cancelScheduled,
  canSchedule,
  scheduleNotification,
  showNotification,
} from '../../platform/notifications.js';

/**
 * Las tres notificaciones del §8, unidas a los datos que las condicionan.
 *
 * El reparto es el de siempre: el DOMINIO decide si toca (`workoutReminderDue`,
 * `weeklyCheckinDue`, `usualTrainingHour`), la PLATAFORMA sabe avisar, y esto
 * de aquí en medio lee lo que hace falta y traduce. Ninguna regla vive aquí.
 *
 * POR QUÉ EL «YA AVISADO HOY» VA EN `meta` Y NO SE SINCRONIZA
 *
 * Es un hecho del DISPOSITIVO, no del usuario: que el móvil te haya recordado
 * el entrenamiento no significa que el portátil deba callarse, ni al revés —de
 * hecho es al revés: entrenando con el móvil en el bolsillo, el aviso útil es
 * el del móvil—. Sincronizarlo convertiría «una vez al día» en «una vez al día
 * en el primer dispositivo que se encienda», que es peor en los dos sentidos.
 */

const CLAVE_ULTIMO_AVISO = 'notif:ultimo-aviso';

interface UltimoAviso {
  /** Día local del último recordatorio de entrenamiento. */
  recordatorio?: string;
  /** Día local del último aviso de check-in. */
  checkin?: string;
}

async function leerUltimoAviso(): Promise<UltimoAviso> {
  const fila = await db.meta.get(CLAVE_ULTIMO_AVISO);
  const valor = fila?.value;
  return typeof valor === 'object' && valor !== null ? (valor as UltimoAviso) : {};
}

async function anotarAviso(tipo: keyof UltimoAviso, dia: string): Promise<void> {
  const previo = await leerUltimoAviso();
  await db.meta.put({ key: CLAVE_ULTIMO_AVISO, value: { ...previo, [tipo]: dia } });
}

/**
 * La franja habitual, sacada del historial real de entrenamientos.
 *
 * No hay ningún campo donde el usuario declare a qué hora entrena, y **no se
 * añade uno**: es un dato que la aplicación ya sabe —cuándo empezaste cada
 * sesión— y preguntarlo sería pedir lo que ya está escrito. Se usan las horas
 * locales de los partes de entrenamiento; la mediana la calcula el dominio.
 */
export async function franjaHabitual(userId: string): Promise<Hour> {
  const logs = await db.workoutLogs.where('userId').equals(userId).toArray();
  const horas = logs
    .filter((log) => log.deletedAt === null)
    .map((log) => new Date(log.startedAt).getHours());
  return usualTrainingHour({ startedHours: horas });
}

export interface RevisionDeAvisos {
  recordatorio: boolean;
  checkin: boolean;
  /** Motivos por los que NO se avisó, para poder mirarlo cuando alguien pregunte. */
  reasonCodes: string[];
}

/**
 * Mira si toca alguno de los dos avisos programables y, en su caso, avisa.
 *
 * El del descanso no está aquí: se dispara solo, desde el temporizador, y no
 * hay nada que decidir.
 *
 * En Android se PROGRAMA para la hora habitual, así que llega con la aplicación
 * cerrada. En Windows no se puede programar nada —la PWA no tiene push— y lo
 * único posible es avisar ahora, con la aplicación abierta. La diferencia no se
 * disimula: `canSchedule()` la dice y aquí se bifurca en consecuencia.
 */
export async function revisarAvisos(
  userId: string,
  ahora = new Date(),
): Promise<RevisionDeAvisos> {
  const salida: RevisionDeAvisos = { recordatorio: false, checkin: false, reasonCodes: [] };

  const prefs = await getPreferences(userId);
  // Sin preferencias todavía no se avisa de nada: el usuario no ha tenido
  // ocasión de decir que sí, y avisar por omisión es cómo se pierde el permiso.
  if (prefs === undefined || !prefs.notificationPrefs.enabled) {
    salida.reasonCodes.push('notifications_disabled');
    return salida;
  }

  // De `ahora`, no del reloj otra vez. Estuvo asi hasta el 06/09/2026: la HORA
  // salia del parametro y la FECHA de `todayLocal()`, o sea que el reloj entraba
  // por parametro a medias. No rompia nada en produccion —los dos leen el mismo
  // reloj— pero dejaba la costura inservible para probar: el test del check-in
  // del lunes tenia que rendirse con un `return` si el dia en que se ejecutaba
  // no era lunes, asi que no comprobaba nada seis dias de cada siete. Un reloj
  // que entra por parametro tiene que entrar entero.
  const hoy = todayLocal(ahora);
  const hora = ahora.getHours();
  const ultimo = await leerUltimoAviso();

  // --- recordatorio de entrenamiento --------------------------------------
  if (prefs.notificationPrefs.workoutReminder) {
    const sesion = await getSessionByDate(userId, hoy);
    const hasPendiente = sesion !== undefined && sesion.status === 'planned';
    const habitual = await franjaHabitual(userId);
    const yaAvisado = ultimo.recordatorio === hoy;
    const decision = workoutReminderDue({
      hasPendingSessionToday: hasPendiente,
      currentHour: hora,
      usualHour: habitual,
      alreadyNotifiedToday: yaAvisado,
    });

    if (sesion !== undefined) {
      const aviso = {
        id: NOTIFICACION.recordatorio,
        titulo: 'Hoy toca entrenar',
        cuerpo: `${sesion.name} · unos ${sesion.estimatedMinutes} min.`,
      };
      // Aqui esta la bifurcacion entre las dos plataformas, y es la unica.
      //
      // En Android, si todavia no ha llegado la hora habitual, se PROGRAMA para
      // esa hora: llega con la aplicacion cerrada, que es de lo que sirve un
      // recordatorio. Si la hora ya paso —abriste la aplicacion por la noche y
      // la sesion sigue sin hacer— se avisa en el momento.
      //
      // En Windows no hay programacion posible sin un servicio de push, asi que
      // solo queda el segundo camino. Es la limitacion que la pantalla de
      // Avisos dice en voz alta en vez de callarse.
      const puedePrograma = canSchedule() && !yaAvisado && hasPendiente && hora < habitual;
      if (puedePrograma) {
        const cuando = new Date(ahora);
        cuando.setHours(habitual, 0, 0, 0);
        salida.recordatorio = await scheduleNotification(aviso, cuando);
        if (salida.recordatorio) await anotarAviso('recordatorio', hoy);
      } else if (decision.due) {
        salida.recordatorio = await showNotification(aviso);
        if (salida.recordatorio) await anotarAviso('recordatorio', hoy);
      } else {
        salida.reasonCodes.push(...decision.reasonCodes);
      }
    } else {
      salida.reasonCodes.push(...decision.reasonCodes);
    }
  } else {
    salida.reasonCodes.push('workout_reminder_off');
  }

  // --- check-in semanal ----------------------------------------------------
  if (prefs.notificationPrefs.weeklyCheckin) {
    const semana = checkinWeekOf(hoy);
    const decision = weeklyCheckinDue({
      today: hoy,
      currentHour: hora,
      doneThisWeek: (await getCheckin(userId, semana)) !== undefined,
      alreadyNotifiedToday: ultimo.checkin === hoy,
    });

    if (decision.due) {
      salida.checkin = await showNotification({
        id: NOTIFICACION.checkin,
        titulo: 'Tu revisión semanal está lista',
        cuerpo: 'Mira cómo fue la semana y qué cambia en la siguiente.',
      });
      if (salida.checkin) await anotarAviso('checkin', hoy);
    } else {
      salida.reasonCodes.push(...decision.reasonCodes);
    }
  } else {
    salida.reasonCodes.push('weekly_checkin_off');
  }

  return salida;
}

/**
 * ¿Quiere el usuario que se le avise del fin del descanso?
 *
 * No hay regla de dominio que consultar —a diferencia de los otros dos avisos,
 * aquí no hay nada que decidir salvo si lo quiere— pero los dos interruptores
 * mandan igual.
 */
async function quiereAvisoDeDescanso(userId: string): Promise<boolean> {
  const prefs = await getPreferences(userId);
  if (prefs === undefined) return false;
  return prefs.notificationPrefs.enabled && prefs.notificationPrefs.restTimer;
}

/**
 * Programa el aviso para cuando el descanso termine (UX-002).
 *
 * ESTO ES LO QUE HACE QUE LLEGUE CON EL MÓVIL EN EL BOLSILLO
 *
 * El aviso se disparaba cuando el temporizador de la pantalla llegaba a cero,
 * o sea que dependía de que la página siguiera viva y corriendo. Con la
 * pantalla apagada Android frena o suspende los temporizadores de JavaScript,
 * así que el aviso llegaba tarde o no llegaba — justo en el caso para el que
 * existe, porque con la aplicación delante ya se ve la cuenta atrás y ya vibra.
 *
 * Programándolo, el aviso lo entrega el SISTEMA a la hora que toca, con la
 * aplicación cerrada si hace falta.
 *
 * Devuelve `false` si no se ha podido programar, y eso NO es un fallo: en
 * Windows la PWA no puede programar nada sin un servicio de push
 * (`scheduleNotification` lo dice sin disimular). Quien llama usa la respuesta
 * para saber si todavía le toca avisar en el momento.
 */
export async function programarFinDeDescanso(userId: string, cuando: Date): Promise<boolean> {
  if (!(await quiereAvisoDeDescanso(userId))) return false;

  return scheduleNotification(
    {
      id: NOTIFICACION.descanso,
      titulo: 'Descanso terminado',
      cuerpo: 'A por la siguiente serie.',
      canal: CANAL.descanso,
      sonoro: true,
    },
    cuando,
  );
}

/**
 * Cancela el aviso programado.
 *
 * Hace falta cuando el descanso deja de tener sentido antes de terminar: se
 * registra la serie siguiente, o se sale de la sesión. Sin esto, el aviso
 * llegaría cuando ya se está levantando otra vez, que es peor que no avisar.
 */
export async function cancelarFinDeDescanso(): Promise<void> {
  await cancelScheduled([NOTIFICACION.descanso]);
}

/**
 * Avisa AHORA de que el descanso ha terminado.
 *
 * El respaldo de `programarFinDeDescanso`, para donde no se puede programar:
 * hoy, Windows. Se dispara con la aplicación delante, que es la única
 * situación en la que se puede.
 *
 * `Descanso` ya vibra al terminar. Esto es para cuando la vibración no basta:
 * la ventana detrás de otra.
 */
export async function avisarFinDeDescanso(userId: string): Promise<boolean> {
  if (!(await quiereAvisoDeDescanso(userId))) return false;

  return showNotification({
    id: NOTIFICACION.descanso,
    titulo: 'Descanso terminado',
    cuerpo: 'A por la siguiente serie.',
    canal: CANAL.descanso,
    sonoro: true,
  });
}

/**
 * Apaga los avisos: cancela además lo que ya estuviera en la cola del sistema.
 *
 * Sin la cancelación, apagar el interruptor dejaría llegando los avisos ya
 * programados. Un ajuste que no surte efecto hasta mañana no es un ajuste.
 */
export async function cancelarTodo(): Promise<void> {
  await cancelScheduled([NOTIFICACION.recordatorio, NOTIFICACION.checkin]);
  logger.info('ui', 'NOTIF_CANCELLED', 'Avisos programados cancelados', {});
}
